// src/context/CurrencyContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import Api from "../helpers/apiHandler";
import { setActiveCurrencyMap } from "../helpers/utility";

const CurrencyContext = createContext();

export const CurrencyProvider = ({ children, companyId }) => {
  const [currency, setCurrency] = useState(null);
  const [currencySymbol, setCurrencySymbol] = useState(null);
  const [currencyName, setCurrencyName] = useState(null);
  const [activeCurrencies, setActiveCurrencies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch default currency from backend
  const fetchDefaultCurrency = useCallback(async () => {
    if (!companyId) {
      // Fallback to USD if no company
      setCurrency("USD");
      setCurrencySymbol("$");
      setCurrencyName("US Dollar");
      setIsLoading(false);
      return;
    }

    try {
      const api = new Api();
      const response = await api.get(`currency/default/${companyId}`);
      
      if (response && response.status === 1 && response.data && response.data.currency) {
        const defaultCurrency = response.data.currency;
        setCurrency(defaultCurrency.currencyCode);
        setCurrencySymbol(defaultCurrency.currencySymbol);
        setCurrencyName(defaultCurrency.currencyName);
        
        // Save to localStorage for persistence
        localStorage.setItem("defaultCurrency", defaultCurrency.currencyCode);
        localStorage.setItem("defaultCurrencySymbol", defaultCurrency.currencySymbol);
        localStorage.setItem("defaultCurrencyName", defaultCurrency.currencyName);
        if (defaultCurrency.id != null && defaultCurrency.id !== "") {
          localStorage.setItem("defaultCurrencyId", String(defaultCurrency.id));
        }
      } else {
        // Fallback to USD if no default is set
        setCurrency("USD");
        setCurrencySymbol("$");
        setCurrencyName("US Dollar");
        localStorage.setItem("defaultCurrency", "USD");
        localStorage.setItem("defaultCurrencySymbol", "$");
        localStorage.setItem("defaultCurrencyName", "US Dollar");
      }
    } catch (error) {
      console.error("Failed to fetch default currency:", error);
      // Fallback to USD on error
      setCurrency("USD");
      setCurrencySymbol("$");
      setCurrencyName("US Dollar");
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  // Load from localStorage initially for faster load
  useEffect(() => {
    const storedCurrency = localStorage.getItem("defaultCurrency");
    const storedSymbol = localStorage.getItem("defaultCurrencySymbol");
    const storedName = localStorage.getItem("defaultCurrencyName");
    
    if (storedCurrency && storedSymbol) {
      setCurrency(storedCurrency);
      setCurrencySymbol(storedSymbol);
      setCurrencyName(storedName);
    }
  }, []);

  // Fetch from backend when companyId is available
  useEffect(() => {
    fetchDefaultCurrency();
  }, [fetchDefaultCurrency]);

  const fetchActiveCurrencies = useCallback(async () => {
    if (!companyId) {
      setActiveCurrencies([]);
      setActiveCurrencyMap([]);
      return;
    }

    try {
      const api = new Api();
      const response = await api.post("currency/list", {
        isMultipart: false,
        data: {
          companyId,
          query: "",
          page: { pageId: 0, limit: 200 },
          isActive: true,
        },
      });

      if (response && response.status === 1 && response.data && response.data.list) {
        const list = response.data.list;
        setActiveCurrencies(list);
        setActiveCurrencyMap(list);
        const storedCode = localStorage.getItem("defaultCurrency");
        const defaultItem =
          list.find((item) => item && (item.isDefault === true || item.isDefault === 1)) ||
          list.find((item) => item && storedCode && item.currencyCode === storedCode);
        if (defaultItem && defaultItem.id != null && defaultItem.id !== "") {
          localStorage.setItem("defaultCurrencyId", String(defaultItem.id));
        }
      } else {
        setActiveCurrencies([]);
        setActiveCurrencyMap([]);
      }
    } catch (error) {
      console.error("Failed to fetch active currencies:", error);
      setActiveCurrencies([]);
      setActiveCurrencyMap([]);
    }
  }, [companyId]);

  const refreshCurrency = useCallback(() => {
    fetchDefaultCurrency();
    fetchActiveCurrencies();
  }, [fetchDefaultCurrency, fetchActiveCurrencies]);

  useEffect(() => {
    fetchActiveCurrencies();
  }, [fetchActiveCurrencies]);

  return (
    <CurrencyContext.Provider 
      value={{ 
        currency, 
        currencySymbol, 
        currencyName,
        activeCurrencies,
        isLoading,
        refreshCurrency,
        setCurrency, 
        setCurrencySymbol,
        setCurrencyName
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);
